import { useState } from "react";
import { Tonal } from "@/components/ui/tonal";
import { Kicker } from "@/components/ui/kicker";
import { useEditorCopy } from "@/lib/project-access";
import {
  FindingsFilter,
  findingMatchesFilter,
  type FindingsFilterStatus,
  type FindingsFilterSeverity,
} from "@/components/experience/findings-filter";
import { FindingRow } from "@/components/experience/finding-row";
import { compareFindingsByCriticality } from "@/components/experience/vocabulary";
import type { CanonicalStatus, FindingType } from "@/components/finding-badges";
import type { Severity } from "./panel-types";

interface CanonicalFindingItem {
  id: string;
  title: string | null;
  description: string | null;
  severity: string | null;
  type: string | null;
  recommendation: string | null;
  representativeFindingId: string | null;
  status?: string | null;
  triageStatus?: string | null;
  state?: string | null;
  stateReason?: string | null;
}

interface FindingsBlockProps {
  projectId: string;
  canonicalFindings: CanonicalFindingItem[];
  hasCompletedWalkthroughs: boolean;
  firstName: string;
}

export function FindingsBlock({
  projectId,
  canonicalFindings,
  hasCompletedWalkthroughs,
  firstName,
}: FindingsBlockProps) {
  const [findingStatus, setFindingStatus] =
    useState<FindingsFilterStatus>("open");
  const [findingSeverity, setFindingSeverity] =
    useState<FindingsFilterSeverity>("all");
  const noRunsCopy = useEditorCopy(
    `Run a walkthrough to see what helps and hinders ${firstName}.`,
    `No walkthrough has run yet. Ask an editor to run one to see what helps and hinders ${firstName}.`,
  );

  const filteredFindings = canonicalFindings
    .filter((f) =>
      findingMatchesFilter(f, {
        status: findingStatus,
        severity: findingSeverity,
      }),
    )
    .sort(compareFindingsByCriticality);

  if (!hasCompletedWalkthroughs) {
    return (
      <Tonal className="space-y-3">
        <Kicker as="h3">Findings</Kicker>
        <p className="text-sm text-muted-foreground">{noRunsCopy}</p>
      </Tonal>
    );
  }

  if (canonicalFindings.length === 0) {
    return (
      <Tonal className="space-y-3">
        <Kicker as="h3">Findings</Kicker>
        <p className="text-sm text-muted-foreground">
          No findings from completed walkthroughs yet.
        </p>
      </Tonal>
    );
  }

  const topFindings = filteredFindings.slice(0, 5);

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-baseline justify-between gap-3 border-b border-border/60 pb-3">
        <div className="space-y-1">
          <Kicker as="h3">Findings</Kicker>
          <p className="text-sm tabular-nums text-muted-foreground">
            {filteredFindings.length} finding
            {filteredFindings.length !== 1 ? "s" : ""} · sorted by criticality
          </p>
        </div>
        <FindingsFilter
          status={findingStatus}
          severity={findingSeverity}
          onStatusChange={setFindingStatus}
          onSeverityChange={setFindingSeverity}
        />
      </header>
      {topFindings.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No findings match the current filters.
        </p>
      ) : (
        <ol className="divide-y divide-border/60">
          {topFindings.map((f) => (
            <FindingItem key={f.id} finding={f} projectId={projectId} />
          ))}
        </ol>
      )}
    </section>
  );
}

function FindingItem({
  finding,
  projectId,
}: {
  finding: CanonicalFindingItem;
  projectId: string;
}) {
  const detailHref = finding.representativeFindingId
    ? `/projects/${projectId}/findings/${finding.representativeFindingId}`
    : null;
  return (
    <li className="py-3 first:pt-0 last:pb-0">
      <FindingRow
        href={detailHref}
        headingLevel="h4"
        finding={{
          id: finding.id,
          title: finding.title ?? "Unnamed finding",
          description: finding.description ?? "",
          type: (finding.type as FindingType | null) ?? "hinders",
          severity: (finding.severity as Severity | null) ?? null,
          status: (finding.status ??
            finding.triageStatus ??
            null) as CanonicalStatus | null,
          state: finding.state ?? null,
          stateReason: finding.stateReason ?? null,
        }}
      />
    </li>
  );
}
